import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from "@mui/material";
import {
  DeductionType,
  GetDriverSettlementDetailsQuery,
  TransactionInput,
} from "../../../graphql/generated";
import TransactionsList from "../TransactionsList/TransactionsList";
import TripLink from "../../common/TripLink";
import { useTranslation } from "react-i18next";
import { capitalize } from "lodash";
import { testIds } from "./DriverSettlementDetails.testIds";

export type DriverSettlementDetailsProps = {
  driverSettlement: NonNullable<
    GetDriverSettlementDetailsQuery["driverSettlementById"]
  >;
  onDeductionAdded: (
    deductionType: DeductionType,
    transaction: TransactionInput
  ) => void;
  onDeductionChanged: (id: string, transaction: TransactionInput) => void;
  onDeductionDeleted: (id: string) => void;
};

const DriverSettlementDetails = ({
  driverSettlement,
  onDeductionAdded,
  onDeductionChanged,
  onDeductionDeleted,
}: DriverSettlementDetailsProps) => {
  const { t } = useTranslation(["finance", "trips"]);
  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        {driverSettlement.pdfDocument.url ? (
          <Button data-testid={testIds.driverSettlementDetailsTrips}
            target="_blank"
            href={driverSettlement.pdfDocument.url}
            variant="contained"
            LinkComponent="a"
          >
            {t("openSettlDoc", "Open Settlement Document")}
          </Button>
        ) : null}
      </Stack>
      <Card
        sx={{
          mb: 2,
        }}
      >
        <CardHeader title={capitalize(t("trips:trips", "Trips"))} />
        <CardContent>
          <Typography>
            {driverSettlement.trips?.map((trip) => (
              <TripLink trip={trip} />
            ))}
          </Typography>
        </CardContent>
      </Card>
      <Typography variant="h4" gutterBottom>
        {t("payments", "Payments")}
      </Typography>
      <TransactionsList
        transactionLabel={t("payment", "Payment")}
        query={() => ({
          data: {
            transactions: {
              data: driverSettlement.payments.map((payment) => {
                const trip = driverSettlement.trips?.find(
                  (trip) => trip._id === payment.trip
                );
                return {
                  ...payment,
                  number: trip ? `Trip ${trip?.tripNumber}` : "N/A",
                };
              }),
              count: driverSettlement.payments.length,
            },
          },
          isLoading: false,
        })}
        onTransactionAdded={() => {}}
        onTransactionChanged={() => {}}
        deleteMutation={() => ({
          mutateAsync: async () => {},
          isLoading: false,
        })}
        editable={false}
      />

      <Typography variant="h4" gutterBottom>
        {t("bonuses", "Bonuses")}
      </Typography>
      <TransactionsList
        transactionLabel={t("bonuses", "Bonuses")}
        isFlatFee
        query={() => ({
          data: {
            transactions: {
              data: driverSettlement.deductions
                .filter((deduction) => deduction.type === DeductionType.Bonus)
                .map((deduction) => ({
                  _id: deduction._id,
                  label: deduction.label,
                  unitType: "Flat Fee",
                  unit: 1,
                  rate: deduction.amount,
                })),
              count: driverSettlement.deductions.filter(
                (deduction) => deduction.type === DeductionType.Bonus
              ).length,
            },
          },
          isLoading: false,
        })}
        onTransactionAdded={onDeductionAdded.bind(null, DeductionType.Bonus)}
        onTransactionChanged={onDeductionChanged}
        deleteMutation={() => ({
          mutateAsync: async ({ id }) => {
            onDeductionDeleted(id);
          },
          isLoading: false,
        })}
      />

      <Typography variant="h4" gutterBottom>
        {t("deductions", "Deductions")}
      </Typography>
      <TransactionsList
        transactionLabel={t("deductions", "Deductions")}
        isFlatFee
        query={() => ({
          data: {
            transactions: {
              data: driverSettlement.deductions
                .filter(
                  (deduction) => deduction.type === DeductionType.Deduction
                )
                .map((deduction) => ({
                  _id: deduction._id,
                  label: deduction.label,
                  unitType: "Flat Fee",
                  unit: 1,
                  rate: -deduction.amount,
                })),
              count: driverSettlement.deductions.filter(
                (deduction) => deduction.type === DeductionType.Deduction
              ).length,
            },
          },
          isLoading: false,
        })}
        onTransactionAdded={(transaction) =>
          onDeductionAdded(DeductionType.Deduction, {
            ...transaction,
            rate: -transaction.rate,
          })
        }
        onTransactionChanged={(transactionId, transaction) => {
          onDeductionChanged(transactionId, {
            ...transaction,
            rate: -transaction.rate,
          });
        }}
        deleteMutation={() => ({
          mutateAsync: async ({ id }) => {
            onDeductionDeleted(id);
          },
          isLoading: false,
        })}
      />
    </Box>
  );
};

export default DriverSettlementDetails;
